import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get, post, upload } from "@/lib/api";
import { toast } from "sonner";
import { CopyCanvas, PageTabs } from "@/components/CopyAnnotator";
import { PageLoader, StatCard } from "@/components/common";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { Trophy, Target, Clock, CheckCircle2, XCircle, MinusCircle, Percent, Award } from "lucide-react";

export default function Result() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("summary");
  const { data, isLoading } = useQuery({ queryKey: ["result", attemptId], queryFn: () => get(`/attempts/${attemptId}/result`) });
  if (isLoading || !data) return <PageLoader />;

  const pie = [
    { name: "Correct", value: data.correct, color: "#22C55E" },
    { name: "Incorrect", value: data.incorrect, color: "#EF4444" },
    { name: "Unattempted", value: data.unattempted, color: "#CBD5E1" },
  ];
  const subjData = Object.entries(data.subject_stats || {}).map(([k, v]) => ({ name: k.slice(0, 8), correct: v.correct, incorrect: v.incorrect, score: Math.round(v.score) }));

  return (
    <div>
      <div className="rt-gradient rounded-2xl p-6 text-white mb-6">
        <p className="text-indigo-200 text-sm">Result</p>
        <h1 className="font-display text-2xl font-extrabold">{data.test_title}</h1>
        <div className="flex flex-wrap items-end gap-6 mt-4">
          <div><p className="text-indigo-200 text-xs">Score</p><p className="font-display text-4xl font-extrabold">{data.score}<span className="text-xl text-indigo-300">/{data.max_score}</span></p></div>
          {data.rank && <div><p className="text-indigo-200 text-xs">Rank</p><p className="font-display text-3xl font-extrabold flex items-center gap-1"><Trophy className="h-6 w-6 text-amber-400" />{data.rank}<span className="text-lg text-indigo-300">/{data.total_participants}</span></p></div>}
          {data.percentile != null && <div><p className="text-indigo-200 text-xs">Percentile</p><p className="font-display text-3xl font-extrabold">{data.percentile}</p></div>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard icon={CheckCircle2} label="Correct" value={data.correct} tone="jade" />
        <StatCard icon={XCircle} label="Incorrect" value={data.incorrect} tone="crimson" />
        <StatCard icon={MinusCircle} label="Skipped" value={data.unattempted} tone="slate" />
        <StatCard icon={Percent} label="Accuracy" value={`${data.accuracy}%`} tone="amber" />
        <StatCard icon={Clock} label="Avg / Q" value={`${data.avg_time_per_q}s`} tone="violet" />
      </div>

      <SubjectiveCopy attemptId={attemptId} />

      <div className="flex gap-2 mb-4">
        {[["summary", "Analysis"], ["review", "Question Review"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`result-tab-${k}`} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === k ? "bg-indigo-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "summary" ? (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="font-display font-bold text-slate-900 mb-4">Answer Distribution</h3>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart><Pie data={pie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>{pie.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie><Tooltip /><Legend /></PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="font-display font-bold text-slate-900 mb-4">Subject-wise Score</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={subjData}><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="correct" fill="#22C55E" radius={[4, 4, 0, 0]} /><Bar dataKey="incorrect" fill="#EF4444" radius={[4, 4, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {data.review.map((r, i) => (
            <div key={r.question_id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`review-q-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="font-medium text-slate-800">Q{i + 1}. {r.question_text}</p>
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-bold ${r.is_correct === true ? "bg-emerald-100 text-emerald-700" : r.is_correct === false ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>{r.is_correct === true ? `+${r.positive_marks}` : r.is_correct === false ? `-${r.negative_marks}` : "0"}</span>
              </div>
              {r.question_image && <img src={r.question_image} alt="" className="mt-3 max-h-56 rounded-lg border" />}
              <div className="mt-3 space-y-1.5">
                {r.options.map((o) => {
                  const isCorrect = r.correct_answer.includes(o.key);
                  const isGiven = r.given.includes(o.key);
                  return <div key={o.key} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${isCorrect ? "bg-emerald-50 text-emerald-800" : isGiven ? "bg-red-50 text-red-700" : "text-slate-600"}`}><span className="font-bold">{o.key}.</span> {o.text} {isCorrect && <CheckCircle2 className="h-4 w-4 ml-auto" />} {isGiven && !isCorrect && <XCircle className="h-4 w-4 ml-auto" />}</div>;
                })}
              </div>
              {(r.explanation || r.explanation_image) && <ExplanationBlock text={r.explanation} image={r.explanation_image} idx={i} />}
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-3 mt-6">
        <button onClick={() => nav("/app/tests")} className="px-5 py-2.5 rounded-lg bg-indigo-800 text-white font-semibold">More Tests</button>
        <button onClick={() => nav("/app/performance")} className="px-5 py-2.5 rounded-lg border border-slate-200 font-semibold text-slate-600 flex items-center gap-2"><Award className="h-4 w-4" /> View Performance</button>
      </div>
    </div>
  );
}

function SubjectiveCopy({ attemptId }) {
  const { data, refetch } = useQuery({ queryKey: ["my-copy", attemptId], queryFn: () => get(`/attempts/${attemptId}/copy`) });
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const c = data?.copy;
  if (!c) return null;
  const fm = (n) => Number(n || 0).toFixed(1);
  const reupload = async (file) => {
    setBusy(true);
    try { const r = await upload(file); await post("/copy-submissions", { attempt_id: attemptId, file_id: r.id }); toast.success("New copy uploaded"); refetch(); }
    catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="subjective-copy-card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h3 className="font-display font-bold text-slate-900">Subjective Answer Copy</h3>
        {c.status === "checked"
          ? <span className="ml-auto font-display font-black text-emerald-600 text-xl" data-testid="subjective-score">{fm(c.total_marks)} / {c.subjective_total_marks ? fm(c.subjective_total_marks) : "—"} marks</span>
          : <span className="ml-auto text-sm font-semibold text-amber-600" data-testid="subjective-status">{c.status === "rescan_requested" ? "Re-scan requested" : "Waiting for teacher to check"}</span>}
      </div>
      {c.status === "rescan_requested" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-2">
          <p>{c.rescan_reason || "Please upload a clearer copy."}</p>
          <label className="inline-block mt-2 bg-red-600 text-white font-bold px-4 py-1.5 rounded-lg cursor-pointer text-xs" data-testid="reupload-copy-btn">
            {busy ? "Uploading..." : "Upload new copy"}
            <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) reupload(f); }} />
          </label>
        </div>
      )}
      {c.status === "checked" && <>
        <p className="text-xs text-slate-500 mb-2">Checked by {c.checked_by_name}</p>
        <div className="flex flex-wrap gap-1.5 mb-3">{(c.marks || []).map((m, i) => <span key={i} className="px-2 py-1 rounded-md text-xs font-bold bg-emerald-50 border border-emerald-200 text-emerald-700" data-testid={`student-q-mark-${i}`}>Q{i + 1}: {fm(m)}</span>)}</div>
        {c.remarks && <p className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900 mb-3" data-testid="student-remarks"><b>Teacher remarks: </b>{c.remarks}</p>}
        <PageTabs pages={c.pages || []} page={page} setPage={setPage} />
        <div className="mt-3 bg-slate-200 rounded-xl overflow-auto max-h-[80vh]">
          {c.pages?.[page] && <CopyCanvas src={c.pages[page]} annotations={(c.annotations || []).filter((a) => (a.page || 0) === page)} marks={c.marks || []} readOnly />}
        </div>
      </>}
    </div>
  );
}

function ExplanationBlock({ text, image, idx }) {
  const [show, setShow] = useState(false);
  if (!show) return <button onClick={() => setShow(true)} data-testid={`show-explanation-${idx}`} className="mt-3 text-sm font-semibold text-indigo-700 hover:underline">View Explanation</button>;
  return (
    <div className="mt-3 p-3 bg-indigo-50 rounded-lg text-sm text-indigo-900" data-testid={`explanation-${idx}`}>
      {text && <p><span className="font-semibold">Explanation: </span>{text}</p>}
      {image && <img src={image} alt="explanation" className="mt-2 max-h-56 rounded-lg border" />}
    </div>
  );
}
